import * as THREE from 'three';

/**
 * Warp tunnel: a long open cylinder around the camera with scrolling light streaks.
 * Child of the camera, so it always points along the view; the ship occludes it via depth.
 */
export class WarpTunnel {
  readonly mesh: THREE.Mesh<THREE.CylinderGeometry, THREE.ShaderMaterial>;
  private intensity = 0;

  constructor() {
    const geo = new THREE.CylinderGeometry(900, 260, 14_000, 48, 1, true);
    geo.rotateX(Math.PI / 2); // axis along Z
    geo.translate(0, 0, -6_500); // extend ahead of the camera
    this.mesh = new THREE.Mesh(
      geo,
      new THREE.ShaderMaterial({
        uniforms: { time: { value: 0 }, intensity: { value: 0 }, tint: { value: new THREE.Color(0.55, 0.8, 1.0) } },
        vertexShader: /* glsl */ `
          #include <common>
          #include <logdepthbuf_pars_vertex>
          varying vec2 vUv;
          void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
            #include <logdepthbuf_vertex>
          }`,
        fragmentShader: /* glsl */ `
          #include <common>
          #include <logdepthbuf_pars_fragment>
          uniform float time;
          uniform float intensity;
          uniform vec3 tint;
          varying vec2 vUv;
          float hash(float n) { return fract(sin(n) * 43758.5453); }
          void main() {
            #include <logdepthbuf_fragment>
            // u: around the tube, v: along it (1 = near the camera).
            float lanes = 180.0;
            float lane = floor(vUv.x * lanes);
            float h = hash(lane);
            float speed = 0.6 + h * 1.4;
            float s = fract(vUv.y * (2.0 + h * 3.0) + time * speed + h * 10.0);
            float streak = smoothstep(0.0, 0.08, s) * (1.0 - smoothstep(0.08, 0.5, s));
            float laneMask = smoothstep(0.42, 0.5, fract(vUv.x * lanes)) * (1.0 - smoothstep(0.5, 0.58, fract(vUv.x * lanes))) * step(0.45, hash(lane * 1.7));
            float fadeEnds = smoothstep(0.0, 0.25, vUv.y) * (1.0 - smoothstep(0.85, 1.0, vUv.y));
            vec3 col = mix(tint, vec3(1.0), h * 0.6) * streak * laneMask * fadeEnds * (0.6 + h);
            col += tint * 0.015 * fadeEnds; // faint haze
            gl_FragColor = vec4(col * intensity * 0.85, 1.0);
          }`,
        side: THREE.BackSide,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 30;
    this.mesh.visible = false;
  }

  /** target: 0 off, ~0.35 spooling, 1 in warp */
  update(dt: number, target: number): void {
    this.intensity += (target - this.intensity) * (1 - Math.exp(-(target > this.intensity ? 3 : 5) * dt));
    const u = this.mesh.material.uniforms;
    u.time.value += dt * (0.4 + this.intensity * 2.5);
    u.intensity.value = this.intensity;
    this.mesh.visible = this.intensity > 0.01;
  }

  get level(): number {
    return this.intensity;
  }
}
